import NextIcon from 'assets/icons/controls/NextIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import { AccordionPanelProps } from 'components/accordion/AccordionPanel';
import { AccordionPanelItemProps } from 'components/accordion/AccordionPanelItem';
import DurationLabel from 'components/DurationLabel';
import {
    WrappingText,
    BodyRegular,
    Flex,
    FlexMdAlignCenter,
    Headline2,
    FlexColLg,
    FlexColRg,
} from 'components/Elements';
import { LearnerCourseContext } from 'contextProviders/LearnerCourseContext';
import useWindowWidth from 'hooks/useWindowWidth';
import { ActivityType, getActivityTypeIcon } from 'models/Activity';
import { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { sizes, theme } from 'theme';
import strings from '../../strings/strings.json';
import Accordion from 'components/accordion/Accordion';
import LearnerCoursePageFooter from 'pages/learner/components/LearnerCoursePageFooter';
import NextLiveTrainingMeetingCard from 'pages/learner/components/NextLiveTrainingMeetingCard';
import NextActivityComponent from './NextActivityComponent';
import { LearnerActivity } from 'models/LearnerCourse';
import { ModuleType } from 'models/Module';
import Header from 'components/sections/Header';
import LearnerCourseDetails from './LearnerCourseDetails';

export default function LearnerCourseView() {
    const { modules, title } = useContext(LearnerCourseContext).learnerCourse!;
    const { setStep, previewMode } = useContext(LearnerCourseContext);
    const { isMobileWidth } = useWindowWidth();

    const activeModuleIndex = useMemo(() => {
        return modules.findIndex((module) => module.activities.some((activity) => !activity.completed));
    }, [modules]);

    let nextLiveTraining: LearnerActivity | undefined = undefined;

    if (!previewMode) {
        nextLiveTraining = modules
            .flatMap((module) => module.activities)
            .filter((x) => x.type === ActivityType.LIVE_TRAINING)
            .filter((x) => new Date(x.startDate ?? 0).getTime() - 60 * 60 * 1000 < new Date().getTime()) // is one hour before the live training starts less than now?
            .filter(
                (x) =>
                    new Date(x.startDate ?? 0).getTime() + (x.durationInMins ?? 0) * 60 * 1000 > new Date().getTime(),
            )[0]; // is now earlier than the end time? get the first that satisfies these filters.
    }

    const moduleProps: AccordionPanelProps[] = modules.map((module, index) => {
        const activityItems: AccordionPanelItemProps[] = [];

        //ADD LEARNER-LED MODULE OVERVIEW
        if (module.moduleType === ModuleType.LEARNER_LED) {
            const overviewItem: AccordionPanelItemProps = {
                action: () => setStep({ id: module.id, stepType: 'Module' }),
                aria: `${strings.learnerCoursePage.moduleOverviewButton.aria}`,
                content: (
                    <>
                        <BodyRegular $bold>{strings.learnerCoursePage.moduleOverviewButton.title}</BodyRegular>
                        <NextIcon size={IconSize.LARGE} />
                    </>
                ),
            };

            activityItems.push(overviewItem);

            //ADD ACTIVITIES
            module.activities
                .filter(
                    (activity) => activity.type === ActivityType.TASK || activity.type === ActivityType.QUESTIONNAIRE,
                )
                .forEach((activity) => {
                    const activityItem: AccordionPanelItemProps = {
                        action: () => setStep({ id: activity.activityStatusId, stepType: 'Activity' }),
                        aria: `${strings.learnerCoursePage.activityButtonAria} ${activity.title}`,
                        content: (
                            <>
                                <FlexMdAlignCenter>
                                    <Flex>{getActivityTypeIcon(activity, IconSize.MEDIUM)}</Flex>
                                    <WrappingText>{activity.title}</WrappingText>
                                </FlexMdAlignCenter>
                                <FlexMdAlignCenter>
                                    {!isMobileWidth && !!activity.durationInMins && (
                                        <DurationLabel durationInMins={activity.durationInMins} hideTitle />
                                    )}
                                    <NextIcon size={IconSize.LARGE} />
                                </FlexMdAlignCenter>
                            </>
                        ),
                    };

                    activityItems.push(activityItem);
                });
        } else {
            //ADD PRESENTATION MODULE WITH LIVE TRAINING
            module.activities
                .filter((activity) => activity.type === ActivityType.LIVE_TRAINING)
                .forEach((activity) => {
                    const activityItem: AccordionPanelItemProps = {
                        action: () => setStep({ id: activity.activityStatusId, stepType: 'Activity' }),
                        aria: `${strings.learnerCoursePage.presentationButtonAria} ${activity.title}`,
                        content: (
                            <>
                                <FlexMdAlignCenter>
                                    <Flex>{getActivityTypeIcon(activity, IconSize.MEDIUM)}</Flex>
                                    <WrappingText>{activity.title}</WrappingText>
                                </FlexMdAlignCenter>
                                <FlexMdAlignCenter>
                                    {!isMobileWidth && !!activity.durationInMins && (
                                        <DurationLabel durationInMins={activity.durationInMins} hideTitle />
                                    )}
                                    <NextIcon size={IconSize.LARGE} />
                                </FlexMdAlignCenter>
                            </>
                        ),
                    };

                    activityItems.push(activityItem);
                });
        }

        const moduleTitle = `Module ${module.order}: ${module.title}`;

        const activities =
            module.moduleType === ModuleType.LEARNER_LED
                ? module.activities.filter(
                      (activity) =>
                          activity.type === ActivityType.TASK || activity.type === ActivityType.QUESTIONNAIRE,
                  )
                : module.activities.filter((activity) => activity.type === ActivityType.LIVE_TRAINING);

        const moduleCompleted = activities.length > 0 && activities.every((activity) => activity.completed);

        const numberOfCompletedActivities = activities.filter((activity) => activity.completed).length;

        const completedActivitiesText = `${numberOfCompletedActivities}/${activities.length}`;

        const modulePanel: AccordionPanelProps = {
            headerTitle: moduleTitle,
            panelCompleted: moduleCompleted,
            completedItemsText: completedActivitiesText,
            panelItems: activityItems,
            openPanel: modules.length === 1 ? true : index === activeModuleIndex,
        };

        return modulePanel;
    });

    return (
        <>
            <Header title={title} />
            <Page>
                <LearnerCourseDetails />
                <Line />
                <LearnerCourseContent>
                    <Headline2>{strings.learnerCoursePage.modulesHeader}</Headline2>
                    {!previewMode &&
                        (nextLiveTraining ? (
                            <NextLiveTrainingMeetingCard nextActivity={nextLiveTraining} />
                        ) : (
                            <NextActivityComponent />
                        ))}
                    <Accordion panels={moduleProps} width={sizes.learnerCourseContentMaxWidth} />
                </LearnerCourseContent>
            </Page>
            <LearnerCoursePageFooter />
        </>
    );
}
const Page = styled(FlexColLg)`
    padding: ${sizes.spacingLg} 0;
    flex: 1 1 auto; /* push trainer footer to bottom */
    justify-content: center;
    width: 100%;
    max-width: ${sizes.appMaxWidthWithoutPadding}px;
`;

const LearnerCourseContent = styled(FlexColRg)`
    flex: 1;
    width: 100%;
    padding: 0 ${sizes.edgePadding};
    max-width: ${sizes.appMaxWidthWithoutPadding}px;
`;

const Line = styled.hr`
    border: 1px solid ${theme.cardBorder};
`;
